import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { errMsg, imgUrl, inr } from "@/lib/api";
import { useAdminApi } from "@/lib/admin";
import { useLoad, PageHead } from "@/components/admin/kit";

const blank = { name: "", price: 0, description: "", image: "", items: [], is_active: true, sort_order: 0 };

const Editor = ({ pkg, onDone }) => {
  const call = useAdminApi();
  const [f, setF] = useState({ ...blank, ...pkg, rupees: (pkg?.price || 0) / 100 });
  const setItem = (i, k, v) => setF({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, [k]: v } : it)) });
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await call("post", "/admin/media", fd);
      setF((x) => ({ ...x, image: r.url }));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const save = async () => {
    const body = { name: f.name, price: Math.round(Number(f.rupees) * 100), description: f.description, image: f.image, items: f.items, is_active: f.is_active, sort_order: Number(f.sort_order) || 0 };
    try {
      if (pkg?.id) await call("put", `/admin/mlm/packages/${pkg.id}`, body);
      else await call("post", "/admin/mlm/packages", body);
      toast.success("Package saved");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-amber-300 bg-white p-5" data-testid="package-editor">
      <div className="grid gap-3 sm:grid-cols-3">
        <Input placeholder="Package name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="pkg-name" />
        <Input type="number" placeholder="Price (₹)" value={f.rupees} onChange={(e) => setF({ ...f, rupees: e.target.value })} data-testid="pkg-price" />
        <Input type="number" placeholder="Sort order" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: e.target.value })} data-testid="pkg-sort" />
      </div>
      <Textarea placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="pkg-description" />
      <div className="flex flex-wrap items-center gap-3">
        {f.image && <img src={imgUrl(f.image)} alt="" className="h-14 w-20 rounded object-cover" />}
        <Input placeholder="Image URL" value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} className="max-w-xs" data-testid="pkg-image" />
        <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} className="text-sm" data-testid="pkg-image-upload" />
      </div>
      <p className="text-xs font-semibold uppercase text-slate-500">Products in this package</p>
      {f.items.map((it, i) => (
        <div key={i} className="flex gap-2">
          <Input placeholder="Product name" value={it.product_name} onChange={(e) => setItem(i, "product_name", e.target.value)} data-testid={`pkg-item-name-${i}`} />
          <Input placeholder="Quantity (e.g. 2 × 1 kg)" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} data-testid={`pkg-item-qty-${i}`} />
          <Button size="icon" variant="ghost" onClick={() => setF({ ...f, items: f.items.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => setF({ ...f, items: [...f.items, { product_name: "", quantity: "" }] })} data-testid="pkg-add-item"><Plus className="h-4 w-4" /> Add product</Button>
      <div className="flex items-center gap-3"><Switch checked={f.is_active} onCheckedChange={(v) => setF({ ...f, is_active: v })} data-testid="pkg-active" /> <span className="text-sm">Visible on website</span></div>
      <div className="flex gap-2"><Button onClick={save} data-testid="pkg-save">Save package</Button><Button variant="outline" onClick={onDone}>Cancel</Button></div>
    </div>
  );
};

export default function Packages({ isAdmin }) {
  const [rows, reload] = useLoad("/admin/mlm/packages");
  const [edit, setEdit] = useState(null);
  const done = () => { setEdit(null); reload(); };
  return (
    <div>
      <PageHead title="Distributor Packages">{isAdmin && <Button onClick={() => setEdit({})} data-testid="pkg-new">New package</Button>}</PageHead>
      {edit && !edit.id && <div className="mb-4"><Editor pkg={null} onDone={done} /></div>}
      <div className="space-y-3">
        {(rows || []).map((p) => edit?.id === p.id ? <Editor key={p.id} pkg={p} onDone={done} /> : (
          <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`admin-pkg-${p.sort_order}`}>
            <div className="flex items-center gap-3"><img src={imgUrl(p.image)} alt="" className="h-12 w-16 rounded object-cover" /><div><p className="font-semibold">{p.name} · {inr(p.price)} {!p.is_active && <span className="text-xs text-red-600">(hidden)</span>}</p><p className="text-xs text-slate-500">{p.items.map((i) => `${i.product_name}${i.quantity ? ` (${i.quantity})` : ""}`).join(", ")}</p></div></div>
            {isAdmin && <Button size="sm" variant="outline" onClick={() => setEdit(p)} data-testid={`pkg-edit-${p.sort_order}`}>Edit</Button>}
          </div>
        ))}
      </div>
    </div>
  );
}
